import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../utils/currency';
import { Project } from '../types';
import { getProject as getProjectAPI } from '../utils/api';
import {
  runMonteCarloSimulation,
  runSensitivityAnalysis,
  generateScenarioAnalysis,
  calculateRiskScore,
  MonteCarloAnalysis,
  ScenarioAnalysis,
  RiskScore,
  SensitivityResult,
} from '../utils/riskAnalysis';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { formatPercent } from '../utils/formatters';
import { LoadingSpinner } from '../components/LoadingSpinner';

export function RiskAnalysisPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadProject() {
      if (!id) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        const p = await getProjectAPI(id);
        setProject(p);
      } catch (err) {
        console.error('Failed to load project:', err);
        setError(err instanceof Error ? err.message : 'Failed to load project');
      } finally {
        setLoading(false);
      }
    }
    
    loadProject();
  }, [id]);

  const runAnalysis = () => {
    if (!project || !project.results) return;

    setIsAnalyzing(true);

    // Simulate loading time
    setTimeout(() => {
      const { results, systemConfig, financialParams } = project;

      // Run Monte Carlo simulation
      const mcResults = runMonteCarloSimulation(
        results.solarData,
        systemConfig,
        financialParams,
        10000
      );
      setMonteCarloResults(mcResults);

      // Run sensitivity analysis
      const sensResults = runSensitivityAnalysis(
        results.solarData,
        systemConfig,
        financialParams
      );
      setSensitivityResults(sensResults);

      // Generate scenarios
      const scenarios = generateScenarioAnalysis(
        results.solarData,
        systemConfig,
        financialParams
      );
      setScenarioResults(scenarios);

      // Calculate risk score
      const risk = calculateRiskScore(
        results.solarData,
        systemConfig,
        financialParams,
        mcResults
      );
      setRiskScore(risk);

      setIsAnalyzing(false);
    }, 2000);
  };

  if (loading) {
    return <LoadingSpinner message={t('riskAnalysis.loading', 'Loading Risk Analysis...')} />;
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-600">{error}</p>
        <Link to="/" className="text-amber-600 hover:underline mt-4 inline-block">
          {t('common.back')}
        </Link>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-600">{t('common.error')}</p>
        <Link to="/" className="text-amber-600 hover:underline mt-4 inline-block">
          {t('common.back')}
        </Link>
      </div>
    );
  }

  if (!project.results) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-600">No analysis results available</p>
        <Link to={`/project/${id}`} className="text-amber-600 hover:underline mt-4 inline-block">
          {t('common.back')}
        </Link>
      </div>
    );
  }

  const getRiskColor = (score: number) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRiskLabel = (score: number) => {
    if (score >= 70) return t('riskAnalysis.lowRisk');
    if (score >= 40) return t('riskAnalysis.mediumRisk');
    return t('riskAnalysis.highRisk');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/project/${id}`}>
            <Button variant="outline" size="sm">
              <ArrowLeft className="size-4 mr-2" />
              {t('common.back')}
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{t('riskAnalysis.title')}</h1>
            <p className="text-slate-600 mt-1">{project.name}</p>
          </div>
        </div>
        {!monteCarloResults && (
          <Button onClick={runAnalysis} disabled={isAnalyzing} size="lg">
            {isAnalyzing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                {t('riskAnalysis.analyzing')}
              </>
            ) : (
              <>
                <BarChart3 className="size-5 mr-2" />
                {t('riskAnalysis.runAnalysis')}
              </>
            )}
          </Button>
        )}
      </div>

      {!monteCarloResults ? (
        <Card>
          <CardContent className="py-16 text-center">
            <AlertTriangle className="size-16 text-amber-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">{t('riskAnalysis.title')}</h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              {t('riskAnalysis.subtitle')}
            </p>
            <Button onClick={runAnalysis} disabled={isAnalyzing} size="lg">
              {isAnalyzing ? t('riskAnalysis.analyzing') : t('riskAnalysis.runAnalysis')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">{t('riskAnalysis.riskScore')}</TabsTrigger>
            <TabsTrigger value="montecarlo">{t('riskAnalysis.monteCarlo')}</TabsTrigger>
            <TabsTrigger value="sensitivity">{t('riskAnalysis.sensitivityAnalysis')}</TabsTrigger>
            <TabsTrigger value="scenarios">{t('riskAnalysis.scenarioComparison')}</TabsTrigger>
          </TabsList>

          {/* Overall Risk Score */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('riskAnalysis.overallRisk')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6">
                    <div className="flex-1">
                      <div className="text-6xl font-bold mb-2 flex items-baseline gap-2">
                        <span className={getRiskColor(riskScore!.overall)}>
                          {riskScore!.overall}
                        </span>
                        <span className="text-2xl text-slate-400">/100</span>
                      </div>
                      <p className={`text-lg font-medium ${getRiskColor(riskScore!.overall)}`}>
                        {getRiskLabel(riskScore!.overall)}
                      </p>
                    </div>
                    <div className="w-24 h-24">
                      <svg className="w-full h-full" viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="10"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke={
                            riskScore!.overall >= 70
                              ? '#10b981'
                              : riskScore!.overall >= 40
                              ? '#f59e0b'
                              : '#ef4444'
                          }
                          strokeWidth="10"
                          strokeDasharray={`${(riskScore!.overall / 100) * 251.2} 251.2`}
                          strokeLinecap="round"
                          transform="rotate(-90 50 50)"
                        />
                      </svg>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Risk Components</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(riskScore!.components).map(([key, value]) => (
                    <div key={key}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium capitalize">
                          {t(`riskAnalysis.${key}Risk` as any) || key}
                        </span>
                        <span className={`text-sm font-semibold ${getRiskColor(value.score)}`}>
                          {value.score}/100
                        </span>
                      </div>
                      <Progress value={value.score} className="h-2" />
                      <p className="text-xs text-slate-500 mt-1">{value.description}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Monte Carlo Results */}
          <TabsContent value="montecarlo" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>
                  {t('riskAnalysis.monteCarlo')} ({monteCarloResults.iterations.toLocaleString()}{' '}
                  {t('riskAnalysis.iterations')})
                </CardTitle>
                <CardDescription>{t('riskAnalysis.probabilityDistribution')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-sm text-red-600 font-medium mb-1">
                      {t('riskAnalysis.worstCase')}
                    </p>
                    <p className="text-2xl font-bold text-red-700">
                      {formatCurrency(
                        convertAmount(monteCarloResults.statistics.npv.p10),
                        currency
                      )}
                    </p>
                    <p className="text-sm text-red-600">
                      IRR: {monteCarloResults.statistics.irr.p10.toFixed(2)}%
                    </p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium mb-1">
                      {t('riskAnalysis.baseCase')}
                    </p>
                    <p className="text-2xl font-bold text-blue-700">
                      {formatCurrency(
                        convertAmount(monteCarloResults.statistics.npv.p50),
                        currency
                      )}
                    </p>
                    <p className="text-sm text-blue-600">
                      IRR: {monteCarloResults.statistics.irr.p50.toFixed(2)}%
                    </p>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-green-600 font-medium mb-1">
                      {t('riskAnalysis.bestCase')}
                    </p>
                    <p className="text-2xl font-bold text-green-700">
                      {formatCurrency(
                        convertAmount(monteCarloResults.statistics.npv.p90),
                        currency
                      )}
                    </p>
                    <p className="text-sm text-green-600">
                      IRR: {monteCarloResults.statistics.irr.p90.toFixed(2)}%
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-2">NPV Statistics</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Mean:</span>
                        <span className="font-medium">
                          {formatCurrency(
                            convertAmount(monteCarloResults.statistics.npv.mean),
                            currency
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Std Dev:</span>
                        <span className="font-medium">
                          {formatCurrency(
                            convertAmount(monteCarloResults.statistics.npv.stdDev),
                            currency
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Min:</span>
                        <span className="font-medium">
                          {formatCurrency(
                            convertAmount(monteCarloResults.statistics.npv.min),
                            currency
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Max:</span>
                        <span className="font-medium">
                          {formatCurrency(
                            convertAmount(monteCarloResults.statistics.npv.max),
                            currency
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">IRR Statistics</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Mean:</span>
                        <span className="font-medium">
                          {monteCarloResults.statistics.irr.mean.toFixed(2)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Std Dev:</span>
                        <span className="font-medium">
                          {monteCarloResults.statistics.irr.stdDev.toFixed(2)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Min:</span>
                        <span className="font-medium">
                          {monteCarloResults.statistics.irr.min.toFixed(2)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Max:</span>
                        <span className="font-medium">
                          {monteCarloResults.statistics.irr.max.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sensitivity Analysis */}
          <TabsContent value="sensitivity" className="space-y-6">
            {sensitivityResults.map((result) => (
              <Card key={result.parameter}>
                <CardHeader>
                  <CardTitle>{result.parameter} Sensitivity</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={result.values.map((val, idx) => ({
                      id: `${result.parameter}-${idx}`,
                      value: val,
                      npv: result.npvResults[idx],
                      irr: result.irrResults[idx],
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="value"
                        tickFormatter={(value) => value.toFixed(2)}
                      />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip
                        formatter={(value: any, name) => {
                          if (name === 'NPV') {
                            return formatCurrency(convertAmount(value), currency);
                          }
                          return formatPercent(value);
                        }}
                      />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="npv"
                        stroke="#3b82f6"
                        name="NPV"
                        strokeWidth={2}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="irr"
                        stroke="#f59e0b"
                        name="IRR (%)"
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Scenario Comparison */}
          <TabsContent value="scenarios" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Conservative */}
              <Card className="border-red-200 bg-red-50/30">
                <CardHeader>
                  <CardTitle className="text-red-700">
                    {t('riskAnalysis.conservative')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-600 mb-2">{t('riskAnalysis.assumptions')}:</p>
                    <ul className="text-xs text-slate-700 space-y-1">
                      {Object.entries(scenarioResults!.conservative.assumptions).map(
                        ([key, value]) => (
                          <li key={key}>
                            • {key}: {value}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                  <div className="pt-4 border-t space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">NPV:</span>
                      <span className="font-semibold">
                        {formatCurrency(
                          convertAmount(scenarioResults!.conservative.results.npv),
                          currency
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">IRR:</span>
                      <span className="font-semibold">
                        {scenarioResults!.conservative.results.irr.toFixed(2)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Payback:</span>
                      <span className="font-semibold">
                        {scenarioResults!.conservative.results.payback.toFixed(1)} {t('projectDetails.years')}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Realistic */}
              <Card className="border-blue-200 bg-blue-50/30">
                <CardHeader>
                  <CardTitle className="text-blue-700">{t('riskAnalysis.realistic')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-600 mb-2">{t('riskAnalysis.assumptions')}:</p>
                    <ul className="text-xs text-slate-700 space-y-1">
                      {Object.entries(scenarioResults!.realistic.assumptions).map(
                        ([key, value]) => (
                          <li key={key}>
                            • {key}: {value}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                  <div className="pt-4 border-t space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">NPV:</span>
                      <span className="font-semibold">
                        {formatCurrency(
                          convertAmount(scenarioResults!.realistic.results.npv),
                          currency
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">IRR:</span>
                      <span className="font-semibold">
                        {scenarioResults!.realistic.results.irr.toFixed(2)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Payback:</span>
                      <span className="font-semibold">
                        {scenarioResults!.realistic.results.payback.toFixed(1)} {t('projectDetails.years')}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Optimistic */}
              <Card className="border-green-200 bg-green-50/30">
                <CardHeader>
                  <CardTitle className="text-green-700">
                    {t('riskAnalysis.optimistic')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-600 mb-2">{t('riskAnalysis.assumptions')}:</p>
                    <ul className="text-xs text-slate-700 space-y-1">
                      {Object.entries(scenarioResults!.optimistic.assumptions).map(
                        ([key, value]) => (
                          <li key={key}>
                            • {key}: {value}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                  <div className="pt-4 border-t space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">NPV:</span>
                      <span className="font-semibold">
                        {formatCurrency(
                          convertAmount(scenarioResults!.optimistic.results.npv),
                          currency
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">IRR:</span>
                      <span className="font-semibold">
                        {scenarioResults!.optimistic.results.irr.toFixed(2)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Payback:</span>
                      <span className="font-semibold">
                        {scenarioResults!.optimistic.results.payback.toFixed(1)} {t('projectDetails.years')}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}